import React, { useEffect, useMemo, useState } from 'react';
import api, { baixarFicheiro, extrairMensagemErro } from '../services/api';
import { obterPerfilSessao, perfilPode } from '../permissions';
import Paginacao, { MetadadosPaginacao, paginacaoVazia } from '../components/Paginacao';
import { downloadBlob, getFilenameFromHeader } from '../services/file';
import { HorizontalScrollContainer } from '../components/common/HorizontalScrollContainer';

type ItemRequisicao = {
  nome: string;
  descricao_tecnica: string;
  quantidade: string;
  unidade: string;
};

type Requisicao = {
  id: string;
  numero_requisicao: string;
  titulo: string;
  descricao: string;
  departamento?: string;
  itens?: ItemRequisicao[];
  valor_estimado?: number;
  moeda: string;
  status: string;
  status_aprovacao: string;
  data_necessidade?: string;
  data_vencimento?: string;
  data_criacao?: string;
  tenant?: { id: string; nome_empresa: string };
  criador?: { nome: string; email: string; perfil?: string };
  propostas?: Array<{ id: string; valor_proposto: number; status: string }>;
};

type HistoricoAprovacao = {
  id: string;
  status: string;
  comentarios?: string | null;
  nivel_alcada?: number;
  valor_limite?: number;
  data_criacao?: string;
  data_decisao?: string;
  aprovador?: { nome: string; email: string; perfil: string };
};

type HistoricoOperacional = {
  id: string;
  acao: string;
  valores_depois?: string | null;
  data_criacao?: string;
  usuario?: { nome: string; email: string; perfil: string };
};
type EventoArmazem = { id: string; tipo: string; data_hora?: string; descricao?: string; resultado?: string; responsavel?: { nome: string; perfil?: string }; prova_visual?: { disponivel: boolean; url?: string; nome?: string } };

type StatusVersaoFactura = 'PENDENTE_VALIDACAO' | 'VALIDADA' | 'INVALIDADA' | 'SUBSTITUIDA';

type VersaoFactura = {
  id: string;
  versao: number;
  nome_original: string;
  mime_type: string;
  status: StatusVersaoFactura;
  motivo_rejeicao: string | null;
  analisado_por?: { id: string; nome: string; email: string } | null;
  data_analise?: string | null;
  data_upload: string;
};

type HistoricoRequisicao = {
  aprovacoes: HistoricoAprovacao[];
  operacional: HistoricoOperacional[];
  armazem: EventoArmazem[];
  facturas: VersaoFactura[];
};

const badgeStatusFactura = (status: string) => {
  switch (status) {
    case 'PENDENTE_VALIDACAO':
      return <span className="status-pill status-warning">Pendente de Validação</span>;
    case 'VALIDADA':
      return <span className="status-pill status-done">Validada</span>;
    case 'INVALIDADA':
      return <span className="status-pill status-blocked">Invalidada</span>;
    case 'SUBSTITUIDA':
      return <span className="status-pill muted">Substituída</span>;
    default:
      return <span className="status-pill muted">{status}</span>;
  }
};

type Filtros = {
  pesquisa: string;
  status: string;
  aprovacao: string;
  data_inicio: string;
  data_fim: string;
};

const unidades = ['Unidade', 'Caixa', 'Litro', 'Quilograma', 'Metro', 'Pacote'];

const itemVazio = (): ItemRequisicao => ({
  nome: '',
  descricao_tecnica: '',
  quantidade: '1',
  unidade: 'Unidade'
});

const labelsStatusRequisicao: Record<string, string> = {
  SOLICITADO: 'Solicitada',
  EM_APROVACAO: 'Em Aprovação',
  EM_COTACAO: 'Em Cotação',
  PROPOSTA_SELECIONADA: 'Proposta Selecionada',
  AGUARDANDO_COMPROVATIVO: 'Aguardando Comprovativo',
  COMPRA_REALIZADA: 'Compra Realizada',
  EM_TRANSITO: 'Em Trânsito',
  CONCLUIDO: 'Concluída'
};

const labelsStatusAprovacao: Record<string, string> = {
  PENDENTE: 'Pendente',
  APROVADA: 'Aprovada',
  REJEITADA: 'Rejeitada'
};

const fluxo = [
  { chave: 'SOLICITADA', label: 'Solicitada' },
  { chave: 'EM_APROVACAO', label: 'Em Aprovação' },
  { chave: 'APROVADA', label: 'Aprovada' },
  { chave: 'EM_COTACAO', label: 'Em Cotação' },
  { chave: 'COMPRA_REALIZADA', label: 'Compra Realizada' },
  { chave: 'EM_TRANSITO', label: 'Em Trânsito' },
  { chave: 'RECEBIDA', label: 'Recebida' },
  { chave: 'CONCLUIDA', label: 'Concluída' }
];

const dataCurta = (valor?: string) => {
  if (!valor) return '-';
  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) return '-';
  return data.toLocaleDateString('pt-PT');
};

const horaCurta = (valor?: string) => {
  if (!valor) return '-';
  const data = new Date(valor);
  if (Number.isNaN(data.getTime())) return '-';
  return data.toLocaleTimeString('pt-PT', { hour: '2-digit', minute: '2-digit' });
};

const labelStatus = (status: string, labels: Record<string, string>) => labels[status] || status;

const textoAcao = (acao: string) =>
  acao
    .replace('CRIAR_REQUISICAO', 'Requisição criada')
    .replace('FECHAR_COMPRA_DIRETA', 'Compra direta registada')
    .replace('SELECIONAR_PROPOSTA_VENCEDORA', 'Proposta vencedora selecionada')
    .replace('FECHAR_COMPRA_PROPOSTA', 'Compra com proposta registada')
    .replace('RECEBER_MERCADORIA', 'Mercadoria recebida')
    .replace('REQUISICAO_APROVAR', 'Requisição aprovada')
    .replace('REQUISICAO_REJEITAR', 'Requisição rejeitada')
    .replace(/_/g, ' ');

const progressoAtual = (req: Requisicao) => {
  if (req.status_aprovacao === 'REJEITADA') return 1;
  if (req.status === 'CONCLUIDO') return 7;
  if (['COMPRA_REALIZADA', 'EM_TRANSITO'].includes(req.status)) return 5;
  if (['PROPOSTA_SELECIONADA', 'AGUARDANDO_COMPROVATIVO'].includes(req.status)) return 4;
  if (req.status === 'EM_COTACAO') return 3;
  if (req.status_aprovacao === 'APROVADA') return 2;
  return 1;
};

const estadoProcesso = (req: Requisicao) => {
  if (req.status_aprovacao === 'REJEITADA') return 'Rejeitada';
  if (req.status === 'SOLICITADO' && req.status_aprovacao === 'PENDENTE') return 'Em Aprovação';
  if (req.status === 'EM_COTACAO') return 'Em Cotação';
  if (req.status === 'PROPOSTA_SELECIONADA') return 'Proposta Selecionada';
  if (req.status === 'AGUARDANDO_COMPROVATIVO') return 'Aguardando Comprovativo';
  if (req.status === 'COMPRA_REALIZADA') return 'Compra Realizada';
  if (req.status === 'EM_TRANSITO') return 'Em Trânsito';
  if (req.status === 'CONCLUIDO') return 'Concluída';
  return labelStatus(req.status, labelsStatusRequisicao);
};

const parseComentarioAuditoria = (evento: HistoricoOperacional) => {
  if (!evento.valores_depois) return '';
  try {
    const valores = JSON.parse(evento.valores_depois);
    return valores.descricao || valores.observacao || valores.observacoes || valores.titulo || valores.status || '';
  } catch {
    return '';
  }
};

const ProvaArmazem: React.FC<{ evento: EventoArmazem }> = ({ evento }) => {
  const [url, setUrl] = useState<string | null>(null);
  const [estado, setEstado] = useState<'parado' | 'carregando' | 'erro'>('parado');
  useEffect(() => {
    let currentUrl = url;
    return () => {
      if (currentUrl) URL.revokeObjectURL(currentUrl);
    };
  }, [url]);
  if (!evento.prova_visual?.disponivel) return <small>Registo antigo sem imagem.</small>;
  const carregar = async () => {
    if (!evento.prova_visual?.url) return;
    setEstado('carregando');
    try {
      const resposta = await api.get(evento.prova_visual.url, { responseType: 'blob' });
      const newUrl = URL.createObjectURL(resposta.data);
      if (url) URL.revokeObjectURL(url);
      setUrl(newUrl);
      setEstado('parado');
    } catch { setEstado('erro'); }
  };
  if (url) return <a href={url} target="_blank" rel="noreferrer"><img src={url} alt={`Prova visual de ${evento.tipo}`} style={{ maxWidth: 220, maxHeight: 140, objectFit: 'cover' }} /></a>;
  return <button className="botao botao-secundario" type="button" onClick={carregar} disabled={estado === 'carregando'}>
    {estado === 'carregando' ? 'A carregar…' : estado === 'erro' ? 'Tentar carregar novamente' : 'Visualizar prova'}
  </button>;
};

const Requisicoes: React.FC = () => {
  const perfil = obterPerfilSessao();
  const podeCriar = perfilPode(perfil, 'requisicoes.criar');
  const [requisicoes, setRequisicoes] = useState<Requisicao[]>([]);
  const [historicos, setHistoricos] = useState<Record<string, HistoricoRequisicao>>({});
  const [selecionadaId, setSelecionadaId] = useState<string | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [paginacao, setPaginacao] = useState<MetadadosPaginacao>(paginacaoVazia);
  const [erro, setErro] = useState<string | null>(null);
  const [mensagem, setMensagem] = useState<string | null>(null);
  const [filtros, setFiltros] = useState<Filtros>({
    pesquisa: '',
    status: '',
    aprovacao: '',
    data_inicio: '',
    data_fim: ''
  });
  const [form, setForm] = useState({
    titulo: '',
    departamento: '',
    data_necessidade: '',
    justificativa: ''
  });
  const [itens, setItens] = useState<ItemRequisicao[]>([itemVazio()]);

  const selecionada = useMemo(
    () => requisicoes.find((req) => req.id === selecionadaId) || null,
    [requisicoes, selecionadaId]
  );

  const carregar = async (pagina = paginacao.pagina) => {
    try {
      setCarregando(true);
      setErro(null);
      const parametros = Object.fromEntries(
        Object.entries(filtros).filter(([, valor]) => Boolean(valor))
      );
      const resposta = await api.get('/requisicoes', { params: { ...parametros, pagina, limite: paginacao.limite } });
      const dados = resposta.data.dados || [];
      setRequisicoes(dados);
      setPaginacao(resposta.data.paginacao || paginacaoVazia);

      const respostasHistorico = await Promise.all(
        dados.map((req: Requisicao) =>
          Promise.all([
            api.get(`/requisicoes/${req.id}/historico-aprovacoes`).catch(() => ({ data: { dados: {} } })),
            api.get(`/requisicoes/${req.id}/facturas/historico`).catch(() => ({ data: { dados: [] } }))
          ]).then(([histRes, factRes]) => [req.id, {
            aprovacoes: histRes.data.dados?.historico_aprovacoes || [],
            operacional: histRes.data.dados?.historico_operacional || [],
            armazem: histRes.data.dados?.historico_armazem || [],
            facturas: factRes.data.dados || []
          }] as const).catch(() => [req.id, { aprovacoes: [], operacional: [], armazem: [], facturas: [] }] as const)
        )
      );
      setHistoricos(Object.fromEntries(respostasHistorico));
    } catch (erroLista: any) {
      setErro(extrairMensagemErro(erroLista, 'Erro ao carregar requisições.'));
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    carregar(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const atualizarItem = (indice: number, campo: keyof ItemRequisicao, valor: string) => {
    setItens((atuais) => atuais.map((item, i) => i === indice ? { ...item, [campo]: valor } : item));
  };

  const adicionarItem = () => setItens((atuais) => [...atuais, itemVazio()]);
  const removerItem = (indice: number) => setItens((atuais) => atuais.filter((_, i) => i !== indice));

  const validarPeriodoExportacao = () => {
    if (!filtros.data_inicio || !filtros.data_fim) return 'Informe data início e data fim.';
    const inicio = new Date(`${filtros.data_inicio}T00:00:00`);
    const fim = new Date(`${filtros.data_fim}T00:00:00`);
    if (fim < inicio) return 'A data fim deve ser maior ou igual à data início.';
    if (fim.getTime() - inicio.getTime() > 30 * 24 * 60 * 60 * 1000) return 'O intervalo máximo para exportação é de 30 dias.';
    return null;
  };

  const exportarAuditoriaPdf = async () => {
    setErro(null);
    setMensagem(null);
    const erroPeriodo = validarPeriodoExportacao();
    if (erroPeriodo) {
      setErro(erroPeriodo);
      return;
    }

    try {
      const resposta = await api.get('/auditoria/export/pdf', {
        params: { data_inicio: filtros.data_inicio, data_fim: filtros.data_fim },
        responseType: 'blob'
      });
      const nome = getFilenameFromHeader(resposta.headers?.['content-disposition'], `auditoria-${filtros.data_inicio}-${filtros.data_fim}.pdf`);
      downloadBlob(resposta.data, nome);
      setMensagem('PDF de auditoria gerado com sucesso.');
    } catch (erroDownload: any) {
      setErro(extrairMensagemErro(erroDownload, 'Erro ao exportar auditoria em PDF.'));
    }
  };

  const exportarRelatorioCompraPdf = async (requisicao: Requisicao) => {
    setErro(null);
    setMensagem(null);
    try {
      const resposta = await api.get(`/compras/${requisicao.id}/relatorio/pdf`, { responseType: 'blob' });
      const nome = getFilenameFromHeader(resposta.headers?.['content-disposition'], `compra-concluida-${requisicao.numero_requisicao}.pdf`);
      downloadBlob(resposta.data, nome);
      setMensagem('Relatório PDF gerado com sucesso.');
    } catch (erroDownload: any) {
      setErro(extrairMensagemErro(erroDownload, 'Erro ao exportar relatório da compra.'));
    }
  };

  const validarFormulario = () => {
    if (!form.titulo.trim()) return 'Informe o título da requisição.';
    if (!form.departamento.trim()) return 'Informe o centro de custo ou departamento.';
    if (!form.justificativa.trim()) return 'Informe a justificativa da necessidade.';
    if (!form.data_necessidade || Number.isNaN(new Date(form.data_necessidade).getTime())) return 'Informe uma data de necessidade válida.';
    if (itens.length === 0) return 'Adicione pelo menos um item.';
    const itemInvalido = itens.find((item) =>
      !item.nome.trim() ||
      !item.descricao_tecnica.trim() ||
      !item.unidade ||
      Number(item.quantidade) <= 0
    );
    return itemInvalido ? 'Cada item precisa de nome, especificação, quantidade válida e unidade.' : null;
  };

  const criar = async (evento: React.FormEvent) => {
    evento.preventDefault();
    if (!podeCriar) return;
    setMensagem(null);
    setErro(null);

    const erroValidacao = validarFormulario();
    if (erroValidacao) {
      setErro(erroValidacao);
      return;
    }

    try {
      const resposta = await api.post('/requisicoes', {
        titulo: form.titulo.trim(),
        departamento: form.departamento.trim(),
        data_necessidade: form.data_necessidade,
        justificativa: form.justificativa.trim(),
        itens: itens.map((item) => ({
          nome: item.nome.trim(),
          descricao_tecnica: item.descricao_tecnica.trim(),
          quantidade: Number(item.quantidade),
          unidade: item.unidade
        }))
      });
      setMensagem(resposta.data.mensagem || 'Requisição criada.');
      setForm({ titulo: '', departamento: '', data_necessidade: '', justificativa: '' });
      setItens([itemVazio()]);
      carregar();
    } catch (erroCriacao: any) {
      setErro(extrairMensagemErro(erroCriacao, 'Erro ao criar requisição.'));
    }
  };

  const historicoSelecionado = selecionada ? historicos[selecionada.id] || { aprovacoes: [], operacional: [], armazem: [], facturas: [] } : { aprovacoes: [], operacional: [], armazem: [], facturas: [] };
  const rejeicao = historicoSelecionado.aprovacoes.find((item) => item.status === 'REJEITADA');
  const eventos = selecionada ? [
    {
      id: `${selecionada.id}-criacao`,
      data: selecionada.data_criacao,
      utilizador: selecionada.criador?.nome || 'Solicitante',
      perfil: selecionada.criador?.perfil || 'SOLICITANTE',
      acao: 'Requisição criada',
      comentario: selecionada.descricao
    },
    ...historicoSelecionado.aprovacoes.map((item) => ({
      id: item.id,
      data: item.data_decisao || item.data_criacao,
      utilizador: item.aprovador?.nome || 'Aprovador',
      perfil: item.aprovador?.perfil || 'APROVADOR',
      acao: item.status === 'APROVADA' ? 'Aprovação concedida' : 'Requisição rejeitada',
      comentario: item.comentarios || ''
    })),
    ...historicoSelecionado.operacional.map((item) => ({
      id: item.id,
      data: item.data_criacao,
      utilizador: item.usuario?.nome || 'Sistema',
      perfil: item.usuario?.perfil || 'OPERACIONAL',
      acao: textoAcao(item.acao),
      comentario: parseComentarioAuditoria(item)
    }))
  ].sort((a, b) => new Date(a.data || 0).getTime() - new Date(b.data || 0).getTime()) : [];

  return (
    <div className="workspace-page requisicoes-page">
      <header className="page-header">
        <div>
          <span className="eyebrow">Solicitante</span>
          <h1>Requisições</h1>
          <p>Documento de necessidade, acompanhamento do fluxo e rastreabilidade do processo.</p>
        </div>
      </header>

      {podeCriar && (
        <form className="panel requisition-form" onSubmit={criar}>
          <div className="panel-heading">
            <h2>Nova requisição</h2>
          </div>

          <div className="form-section">
            <h3>Dados gerais</h3>
            <div className="form-row form-row-3">
              <label>
                Título da requisição
                <input value={form.titulo} onChange={(e) => setForm({ ...form, titulo: e.target.value })} required />
              </label>
              <label>
                Centro de custo ou departamento
                <input value={form.departamento} onChange={(e) => setForm({ ...form, departamento: e.target.value })} required />
              </label>
              <label>
                Data de necessidade
                <input type="date" value={form.data_necessidade} onChange={(e) => setForm({ ...form, data_necessidade: e.target.value })} required />
              </label>
            </div>
            <label>
              Justificativa da necessidade
              <textarea value={form.justificativa} onChange={(e) => setForm({ ...form, justificativa: e.target.value })} required />
            </label>
          </div>

          <div className="form-section">
            <div className="section-title-row">
              <h3>Itens solicitados</h3>
              <button className="botao botao-secundario" type="button" onClick={adicionarItem}>Adicionar item</button>
            </div>

            <div className="item-editor-list">
              {itens.map((item, indice) => (
                <div className="item-editor" key={indice}>
                  <label>
                    Nome do item
                    <input value={item.nome} onChange={(e) => atualizarItem(indice, 'nome', e.target.value)} required />
                  </label>
                  <label>
                    Especificação
                    <input value={item.descricao_tecnica} onChange={(e) => atualizarItem(indice, 'descricao_tecnica', e.target.value)} required />
                  </label>
                  <label>
                    Quantidade
                    <input type="number" min="0.01" step="0.01" value={item.quantidade} onChange={(e) => atualizarItem(indice, 'quantidade', e.target.value)} required />
                  </label>
                  <label>
                    Unidade
                    <select value={item.unidade} onChange={(e) => atualizarItem(indice, 'unidade', e.target.value)} required>
                      {unidades.map((unidade) => <option key={unidade} value={unidade}>{unidade}</option>)}
                    </select>
                  </label>
                  <button className="botao botao-perigo" type="button" onClick={() => removerItem(indice)} disabled={itens.length === 1}>Remover</button>
                </div>
              ))}
            </div>
          </div>

          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '0.25rem' }}>
            <button className="botao botao-primario" type="submit">Criar requisição</button>
          </div>
        </form>
      )}

      <section className="panel requisition-list-panel">
        <div className="panel-heading">
          <h2>Consulta operacional</h2>
          <button className="botao botao-secundario" type="button" onClick={() => carregar(paginacao.pagina)}>Atualizar</button>
        </div>

        {mensagem && <div className="alerta alerta-sucesso">{mensagem}</div>}
        {erro && <div className="alerta alerta-erro">{erro}</div>}

        <div className="filters-grid">
          <label>
            Pesquisar
            <input placeholder="Número, título ou departamento" value={filtros.pesquisa} onChange={(e) => setFiltros({ ...filtros, pesquisa: e.target.value })} />
          </label>
          <label>
            Estado
            <select value={filtros.status} onChange={(e) => setFiltros({ ...filtros, status: e.target.value })}>
              <option value="">Todos</option>
              <option value="SOLICITADO">Solicitada</option>
              <option value="EM_COTACAO">Em Cotação</option>
              <option value="PROPOSTA_SELECIONADA">Proposta Selecionada</option>
              <option value="AGUARDANDO_COMPROVATIVO">Aguardando Comprovativo</option>
              <option value="COMPRA_REALIZADA">Compra Realizada</option>
              <option value="EM_TRANSITO">Em Trânsito</option>
              <option value="CONCLUIDO">Concluída</option>
            </select>
          </label>
          <label>
            Aprovação
            <select value={filtros.aprovacao} onChange={(e) => setFiltros({ ...filtros, aprovacao: e.target.value })}>
              <option value="">Todas</option>
              <option value="PENDENTE">Pendente</option>
              <option value="APROVADA">Aprovada</option>
              <option value="REJEITADA">Rejeitada</option>
            </select>
          </label>
          <label>
            Início
            <input type="date" value={filtros.data_inicio} onChange={(e) => setFiltros({ ...filtros, data_inicio: e.target.value })} />
          </label>
          <label>
            Fim
            <input type="date" value={filtros.data_fim} onChange={(e) => setFiltros({ ...filtros, data_fim: e.target.value })} />
          </label>
          {perfil === 'GERENTE_EMPRESA' && (
            <label>
              Exportação
              <button className="botao botao-primario" type="button" onClick={exportarAuditoriaPdf}>Exportar PDF</button>
            </label>
          )}
          <button className="botao botao-primario" type="button" onClick={() => carregar(1)}>Filtrar</button>
        </div>

        {carregando ? (
          <div className="inline-loading"><div className="spinner"></div><span>Carregando...</span></div>
        ) : (
          <HorizontalScrollContainer className="table-shell">
            <table>
              <thead>
                <tr>
                  <th>Número</th>
                  <th>Título</th>
                  <th>Departamento</th>
                  <th>Criação</th>
                  <th>Necessidade</th>
                  <th>Fluxo</th>
                  <th>Aprovação</th>
                  <th>Consulta</th>
                </tr>
              </thead>
              <tbody>
                {requisicoes.map((req) => (
                  <tr key={req.id}>
                    <td>{req.numero_requisicao}</td>
                    <td><strong>{req.titulo}</strong><small>{req.criador?.nome || 'Sem solicitante informado'}</small></td>
                    <td>{req.departamento || 'Nao informado'}</td>
                    <td>{dataCurta(req.data_criacao)}</td>
                    <td>{dataCurta(req.data_necessidade || req.data_vencimento)}</td>
                    <td><span className="status-pill">{estadoProcesso(req)}</span></td>
                    <td><span className={`status-pill ${req.status_aprovacao === 'REJEITADA' ? 'status-blocked' : 'muted'}`}>{labelStatus(req.status_aprovacao, labelsStatusAprovacao)}</span></td>
                    <td><button className="botao botao-secundario" type="button" onClick={() => setSelecionadaId(req.id)}>Ver detalhe</button></td>
                  </tr>
                ))}
                {requisicoes.length === 0 && (
                  <tr><td colSpan={8}>Nenhuma requisição encontrada para os filtros atuais.</td></tr>
                )}
              </tbody>
            </table>
            <Paginacao paginacao={paginacao} carregando={carregando} aoMudarPagina={carregar} />
          </HorizontalScrollContainer>
        )}
      </section>

      {selecionada && (
        <div className="modal-overlay requisition-modal-overlay" role="dialog" aria-modal="true">
          <div className="modal-conteudo requisition-detail-modal">
            <div className="panel-heading">
              <div>
                <span className="eyebrow">{selecionada.numero_requisicao}</span>
                <h2>{selecionada.titulo}</h2>
              </div>
              <div className="inline-actions">
                {['GERENTE_EMPRESA', 'APROVADOR', 'SUPER_ADMIN'].includes(String(perfil)) && selecionada.status === 'CONCLUIDO' && (
                  <button className="botao botao-primario" type="button" onClick={() => exportarRelatorioCompraPdf(selecionada)}>Exportar relatório PDF</button>
                )}
                <button className="botao botao-secundario" type="button" onClick={() => setSelecionadaId(null)}>Fechar</button>
              </div>
            </div>

            {rejeicao && (
              <div className="rejection-box">
                <strong>Rejeição registada</strong>
                <span>{rejeicao.aprovador?.nome || 'Responsável não informado'} em {dataCurta(rejeicao.data_decisao || rejeicao.data_criacao)}</span>
                <p>{rejeicao.comentarios || 'Motivo não informado.'}</p>
              </div>
            )}

            <section className="detail-section">
              <h3>Dados gerais</h3>
              <dl className="meta-grid">
                <div><dt>Número</dt><dd>{selecionada.numero_requisicao}</dd></div>
                <div><dt>Solicitante</dt><dd>{selecionada.criador?.nome || '-'}</dd></div>
                <div><dt>Departamento</dt><dd>{selecionada.departamento || 'Nao informado'}</dd></div>
                <div><dt>Data de criação</dt><dd>{dataCurta(selecionada.data_criacao)}</dd></div>
                <div><dt>Data de necessidade</dt><dd>{dataCurta(selecionada.data_necessidade || selecionada.data_vencimento)}</dd></div>
                <div><dt>Aprovação</dt><dd>{labelStatus(selecionada.status_aprovacao, labelsStatusAprovacao)}</dd></div>
              </dl>
              <p className="justification-text">{selecionada.descricao}</p>
            </section>

            <section className="detail-section">
              <h3>Itens</h3>
              <HorizontalScrollContainer className="table-shell">
                <table>
                  <thead><tr><th>Item</th><th>Especificação</th><th>Quantidade</th><th>Unidade</th></tr></thead>
                  <tbody>
                    {(Array.isArray(selecionada.itens) ? selecionada.itens : []).map((item, indice) => (
                      <tr key={`${item.nome}-${indice}`}>
                        <td>{item.nome}</td>
                        <td>{item.descricao_tecnica}</td>
                        <td>{item.quantidade}</td>
                        <td>{item.unidade}</td>
                      </tr>
                    ))}
                    {(!Array.isArray(selecionada.itens) || selecionada.itens.length === 0) && (
                      <tr><td colSpan={4}>Itens estruturados não registados nesta requisição.</td></tr>
                    )}
                  </tbody>
                </table>
              </HorizontalScrollContainer>
            </section>

            <section className="detail-section">
              <h3>Progresso</h3>
              <HorizontalScrollContainer>
                <div className={`timeline-progress ${selecionada.status_aprovacao === 'REJEITADA' ? 'rejected' : ''}`}>
                  {fluxo.map((etapa, indice) => (
                    <div className={`timeline-step ${indice <= progressoAtual(selecionada) ? 'active' : ''}`} key={etapa.chave}>
                      <span>{indice + 1}</span>
                      <strong>{etapa.label}</strong>
                    </div>
                  ))}
                </div>
              </HorizontalScrollContainer>
            </section>

            <section className="detail-section">
              <h3>Histórico</h3>
              <div className="history-list">
                {eventos.map((evento) => (
                  <div className="history-item" key={evento.id}>
                    <time>{dataCurta(evento.data)}<span>{horaCurta(evento.data)}</span></time>
                    <div>
                      <strong>{evento.acao}</strong>
                      <small>{evento.utilizador} · {evento.perfil}</small>
                      {evento.comentario && <p>{evento.comentario}</p>}
                    </div>
                  </div>
                ))}
                {eventos.length === 0 && <div className="empty-state">Ainda não há eventos registados.</div>}
              </div>
            </section>
            {historicoSelecionado.armazem.length > 0 && (
              <section className="detail-section">
                <h3>Recebimentos e ocorrências do Armazém</h3>
                <div className="history-list">
                  {historicoSelecionado.armazem.map((evento) => <div className="history-item" key={evento.id}>
                    <time>{dataCurta(evento.data_hora)}<span>{horaCurta(evento.data_hora)}</span></time>
                    <div><strong>{evento.tipo}</strong><small>{evento.responsavel?.nome || 'Armazém'} · {evento.resultado || '-'}</small>
                      <p>{evento.descricao || 'Sem descrição registada.'}</p><ProvaArmazem evento={evento} />
                    </div>
                  </div>)}
                </div>
              </section>
            )}
            {historicoSelecionado.facturas && historicoSelecionado.facturas.length > 0 && (
              <section className="detail-section">
                <h3>Histórico de Facturas / Documentos</h3>
                <div className="stacked-list">
                  {historicoSelecionado.facturas.map((versao: VersaoFactura) => (
                    <div className="list-row" key={versao.id} style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', padding: '0.75rem', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '6px', marginBottom: '0.5rem' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span>
                          <strong>Versão {versao.versao}</strong> · {versao.nome_original}
                          <small style={{ display: 'block', color: '#64748b' }}>Enviado em: {dataCurta(versao.data_upload)}</small>
                        </span>
                        <div>{badgeStatusFactura(versao.status)}</div>
                      </div>
                      {versao.motivo_rejeicao && (
                        <div className="alerta alerta-erro" style={{ margin: 0, padding: '0.5rem', fontSize: '0.9rem' }}>
                          <strong>Motivo da invalidação:</strong> {versao.motivo_rejeicao}
                          {versao.analisado_por && <small style={{ display: 'block' }}>Por: {versao.analisado_por.nome} em {dataCurta(versao.data_analise || undefined)}</small>}
                        </div>
                      )}
                      <div className="inline-actions" style={{ display: 'flex', gap: '0.5rem', marginTop: '0.25rem' }}>
                        <button className="botao" type="button" onClick={async () => {
                          try {
                            await baixarFicheiro(`/facturas/versoes/${versao.id}/download`, versao.nome_original || 'factura.pdf');
                          } catch (err) {
                            setErro('Erro ao baixar documento da factura.');
                          }
                        }}>
                          Baixar documento
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Requisicoes;
